/**
 * 월주(月柱) 계산.
 *
 * 사주의 월은 절(節) 기준으로 결정된다 (양력 월과 다름).
 * 각 절은 새로운 사주월의 시작이며 고정된 지지에 대응한다:
 *
 *   입춘(315°) → 寅(2)   경칩(345°) → 卯(3)   청명(15°)  → 辰(4)
 *   입하(45°)  → 巳(5)   망종(75°)  → 午(6)   소서(105°) → 未(7)
 *   입추(135°) → 申(8)   백로(165°) → 酉(9)   한로(195°) → 戌(10)
 *   입동(225°) → 亥(11)  대설(255°) → 子(0)   소한(285°) → 丑(1)
 *
 * 월간은 오호둔월법으로 산출한다:
 *   그룹 0 (甲己년) → 인월 시작 천간 丙(2)
 *   그룹 1 (乙庚년) → 인월 시작 천간 戊(4)
 *   그룹 2 (丙辛년) → 인월 시작 천간 庚(6)
 *   그룹 3 (丁壬년) → 인월 시작 천간 壬(8)
 *   그룹 4 (戊癸년) → 인월 시작 천간 甲(0)
 */

import type { Pillar } from '../types/pillars';
import type { HeavenlyStem } from '../types/stems';
import type { EarthlyBranch } from '../types/branches';
import { MONTH_STEM_START, HEAVENLY_STEMS, EARTHLY_BRANCHES } from '../core/constants';
import { makePillarFromStemBranch, stemIndex } from '../core/sexagenary';
import { dateToJD } from '../astronomy/julian-day';
import { findCurrentJeolgi } from '../astronomy/solar-terms';

/**
 * 월주를 계산한다.
 *
 * @param yearStem - 년주 천간
 * @param year - 양력 연도
 * @param month - 월 (1-12)
 * @param day - 일
 * @param hour - 시 (0-23)
 * @param minute - 분 (0-59)
 * @returns 월주
 */
export function getMonthPillar(
  yearStem: HeavenlyStem,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): Pillar {
  const birthJD = dateToJD(year, month, day, hour, minute);

  // Find the current 절(節) to determine the saju month
  const jeolgi = findCurrentJeolgi(birthJD, year);

  if (!jeolgi) {
    throw new Error(`Cannot determine month for date ${year}-${month}-${day}`);
  }

  const branchIdx = jeolgi.branchIndex;
  const branch = EARTHLY_BRANCHES[branchIdx] as EarthlyBranch;

  // 오호둔월법: calculate month stem from year stem group
  const yearStemIdx = stemIndex(yearStem);
  const group = yearStemIdx % 5;
  const startStemIdx = MONTH_STEM_START[group];

  // Month offset from 寅(branchIndex 2): offset 0 = 寅, 1 = 卯, ..., 11 = 丑
  const monthOffset = ((branchIdx - 2) + 12) % 12;
  const monthStemIdx = (startStemIdx + monthOffset) % 10;
  const stem = HEAVENLY_STEMS[monthStemIdx] as HeavenlyStem;

  return makePillarFromStemBranch(stem, branch);
}
